import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import {
  CatalogOffering,
  CatalogService,
  CatalogWarehouseBin,
  InboundShipment,
  WarehouseTask
} from './catalog.service';
import { InventoryCostingApiService, LandedCostAllocation } from './inventory-costing-api.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-inbound-shipment-detail',
  imports: [
    AinPageComponent, AinStateComponent, InfoHintComponent, InventoryPageHintComponent, RouterLink, FormsModule,
    ButtonModule, CheckboxModule, InputTextModule, InputNumberModule, SelectModule, TableModule,
    CurrencySelectorComponent, AinCanAccessDirective
  ],
  template: `
    <ain-page [title]="t('catalog.inbound.detailTitle')" [description]="shipment()?.referenceNumber || ''">
      <ain-inventory-page-hint pageKey="inboundDetail" />
      <ain-info-hint page-title-addon
        [titleKey]="'catalog.costing.landedCost.help.title'"
        [descriptionKey]="'catalog.costing.landedCost.help.description'"
        [bulletKeys]="['catalog.costing.landedCost.help.bullets.allocation', 'catalog.costing.landedCost.help.bullets.commit']" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!shipment()) {
        <ain-state type="empty" [title]="t('catalog.inbound.notFound')" />
      } @else {
        <section class="head">
          <a [routerLink]="['/inventory/inbound-shipments']">{{ t('catalog.action.backToList') }}</a>
          <span>{{ t('columns.status') }}: <strong>{{ shipment()!.status }}</strong></span>
          @if (shipment()!.status === 'Draft') {
            <button pButton type="button" [label]="t('catalog.inbound.schedule')" (click)="schedule()"></button>
          }
        </section>

        <div class="tabs">
          <button type="button" class="tab-btn" [class.active]="activeTab() === 'receiving'" (click)="activeTab.set('receiving')">{{ t('catalog.inbound.receiveTitle') }}</button>
          <button type="button" class="tab-btn" [class.active]="activeTab() === 'landedCost'" (click)="activeTab.set('landedCost'); loadLandedCosts()">{{ t('catalog.costing.landedCost.tab') }}</button>
        </div>

        @if (activeTab() === 'receiving') {
        <p-table [value]="shipment()!.lines">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('catalog.inventory.offeringId') }}</th>
              <th>{{ t('catalog.inbound.expectedQty') }}</th>
              <th>{{ t('catalog.inbound.receivedQty') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-line>
            <tr>
              <td>{{ offeringLabel(line.offeringId) }}</td>
              <td>{{ line.expectedQuantity }}</td>
              <td>{{ line.receivedQuantity }}</td>
            </tr>
          </ng-template>
        </p-table>

        @if (canReceive()) {
          <section class="form-card">
            <h3>{{ t('catalog.inbound.receiveTitle') }}</h3>
            <div class="form-grid">
              <label><span>{{ t('catalog.inbound.line') }}</span><p-select [(ngModel)]="receiveForm.lineId" [options]="lineOptions()" optionLabel="label" optionValue="value" appendTo="body" /></label>
              <label><span>{{ t('catalog.inventory.quantity') }}</span><input pInputText type="number" min="0" step="0.001" [(ngModel)]="receiveForm.quantity" /></label>
              <label><span>{{ t('catalog.inbound.targetBin') }}</span><p-select [(ngModel)]="receiveForm.targetBinId" [options]="binOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" /></label>
              <label class="check"><p-checkbox [binary]="true" [(ngModel)]="receiveForm.directPutAway" /><span>{{ t('catalog.inbound.directPutAway') }}</span></label>
            </div>
            <button pButton type="button" [label]="t('catalog.inbound.receive')" (click)="receive()"></button>
          </section>
        }

        <section class="list-card">
          <h3>{{ t('catalog.inbound.putAwayTasks') }}</h3>
          <p-table [value]="tasks()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('catalog.inventory.quantity') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-task>
              <tr>
                <td>{{ task.status }}</td>
                <td>{{ task.quantity }}</td>
                <td>
                  @if (task.status === 'Pending') {
                    <button pButton type="button" size="small" [label]="t('catalog.inbound.completePutAway')" (click)="completePutAway(task)"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        </section>
        }

        @if (activeTab() === 'landedCost') {
          <section class="list-card">
            <h3>{{ t('catalog.costing.landedCost.allocationsTitle') }}</h3>
            @if (landedCostPermissionDenied()) {
              <ain-state type="error" [title]="t('catalog.costing.states.noPermission')" />
            } @else {
              <p-table [value]="landedCosts()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('catalog.inventory.reference') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('catalog.costing.landedCost.method') }}</th>
                    <th>{{ t('catalog.costing.landedCost.totalAmount') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td><button type="button" class="link-btn" (click)="selectAllocation(row)">{{ row.referenceNumber }}</button></td>
                    <td>{{ row.status }}</td>
                    <td>{{ allocationMethodLabel(row.allocationMethod) }}</td>
                    <td>{{ row.totalLandedCostAmount }} {{ row.currencyCode }}</td>
                  </tr>
                </ng-template>
              </p-table>

              <div class="form-card" ainCanAccess [ainCanAccessPermission]="'inventory.landed-cost.manage'">
                <h4>{{ selectedAllocation() ? t('catalog.costing.landedCost.editAllocation') : t('catalog.costing.landedCost.newAllocation') }}</h4>
                <div class="form-grid">
                  <label><span>{{ t('catalog.inventory.reference') }}</span><input pInputText [(ngModel)]="landedForm.referenceNumber" /></label>
                  <label><span>{{ t('catalog.costing.landedCost.method') }}</span>
                    <p-select [(ngModel)]="landedForm.allocationMethod" [options]="allocationMethodOptions()" optionLabel="label" optionValue="value" appendTo="body" />
                  </label>
                  <label><span>{{ t('catalog.costing.landedCost.totalAmount') }}</span><p-inputNumber [(ngModel)]="landedForm.totalLandedCostAmount" [minFractionDigits]="2" /></label>
                  <label><span>{{ t('columns.currency') }}</span><ain-currency-selector [(ngModel)]="landedForm.currencyCode" /></label>
                  <label><span>{{ t('catalog.costing.landedCost.reason') }}</span><input pInputText [(ngModel)]="landedForm.reason" /></label>
                </div>
                @if (landedForm.allocationMethod === 'Weight') {
                  <p-table [value]="shipment()!.lines">
                    <ng-template pTemplate="header">
                      <tr>
                        <th>{{ t('catalog.inventory.offeringId') }}</th>
                        <th>{{ t('catalog.costing.landedCost.weight') }}</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-line>
                      <tr>
                        <td>{{ offeringLabel(line.offeringId) }}</td>
                        <td><p-inputNumber [(ngModel)]="lineWeights[line.id]" [min]="0" /></td>
                      </tr>
                    </ng-template>
                  </p-table>
                }
                <div class="actions">
                  @if (!selectedAllocation()) {
                    <button pButton type="button" [label]="t('catalog.action.create')" [disabled]="landedBusy()" (click)="createLandedCost()"></button>
                  } @else {
                    <button pButton type="button" severity="secondary" [label]="t('catalog.costing.landedCost.preview')" [disabled]="landedBusy()" (click)="previewLandedCost()"></button>
                    @if (selectedAllocation()!.status === 'Draft') {
                      <button pButton type="button" [label]="t('catalog.costing.landedCost.commit')" [disabled]="landedBusy()" (click)="commitLandedCost()"></button>
                      <button pButton type="button" severity="danger" [label]="t('catalog.costing.landedCost.cancel')" [disabled]="landedBusy()" (click)="cancelLandedCost()"></button>
                    }
                  }
                </div>
                @if (selectedAllocation()?.lines?.length) {
                  <h4>{{ t('catalog.costing.landedCost.previewLines') }}</h4>
                  <p-table [value]="selectedAllocation()!.lines">
                    <ng-template pTemplate="header">
                      <tr>
                        <th>{{ t('catalog.inventory.offeringId') }}</th>
                        <th>{{ t('catalog.inbound.receivedQty') }}</th>
                        <th>{{ t('catalog.costing.landedCost.receiptValue') }}</th>
                        <th>{{ t('catalog.costing.landedCost.allocatedAmount') }}</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-line>
                      <tr>
                        <td>{{ offeringLabel(line.offeringId) }}</td>
                        <td>{{ line.receivedQuantity }}</td>
                        <td>{{ line.receiptValue }}</td>
                        <td>{{ line.allocatedAmount }}</td>
                      </tr>
                    </ng-template>
                  </p-table>
                }
              </div>
            }
          </section>
        }
        @if (error()) { <p class="error">{{ error() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .head { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-bottom: 12px; }
    .tabs { display: flex; gap: 8px; margin-bottom: 12px; }
    .tab-btn { border: 1px solid #e2e8f0; background: #fff; border-radius: 8px; padding: 8px 12px; cursor: pointer; font-weight: 600; }
    .tab-btn.active { background: #eff6ff; border-color: #93c5fd; color: #1d4ed8; }
    .form-card, .list-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin: 12px 0; background: #fff; }
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; }
    .check { display: flex; align-items: center; gap: 8px; }
    .actions { display: flex; gap: 8px; margin-top: 10px; flex-wrap: wrap; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
  `]
})
export class InventoryInboundShipmentDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(CatalogService);
  private readonly costingApi = inject(InventoryCostingApiService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly activeTab = signal<'receiving' | 'landedCost'>('receiving');
  readonly landedCosts = signal<LandedCostAllocation[]>([]);
  readonly selectedAllocation = signal<LandedCostAllocation | null>(null);
  readonly landedBusy = signal(false);
  readonly landedCostPermissionDenied = signal(false);
  readonly shipment = signal<InboundShipment | null>(null);
  readonly tasks = signal<WarehouseTask[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly bins = signal<CatalogWarehouseBin[]>([]);

  readonly receiveForm = {
    lineId: '',
    quantity: 0,
    targetBinId: '',
    directPutAway: false,
    idempotencyCounter: 0
  };

  readonly canReceive = computed(() => {
    const s = this.shipment();
    return s && (s.status === 'Scheduled' || s.status === 'Receiving' || s.status === 'PartiallyReceived');
  });

  allocationMethodOptions(): { label: string; value: string }[] {
    return [
      { label: this.t('catalog.costing.landedCost.methods.Quantity'), value: 'Quantity' },
      { label: this.t('catalog.costing.landedCost.methods.ReceiptValue'), value: 'ReceiptValue' },
      { label: this.t('catalog.costing.landedCost.methods.Weight'), value: 'Weight' }
    ];
  }

  landedForm = {
    referenceNumber: '',
    allocationMethod: 'Quantity',
    totalLandedCostAmount: 0,
    currencyCode: 'USD',
    reason: ''
  };

  lineWeights: Record<string, number> = {};

  t(key: string): string {
    return this.localization.translate(key);
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.reload(id, false)
    );
    this.service.listOfferings().subscribe({ next: o => this.offerings.set(o) });
    void this.reload(id);
  }

  offeringLabel(id: string): string {
    const o = this.offerings().find(x => x.id === id);
    return o ? `${o.code} — ${o.title}` : '-';
  }

  lineOptions = () =>
    (this.shipment()?.lines ?? []).map(l => ({
      label: `${this.offeringLabel(l.offeringId)} (${l.receivedQuantity}/${l.expectedQuantity})`,
      value: l.id
    }));

  binOptions = () => this.bins().map(b => ({ label: `${b.code}`, value: b.id }));

  schedule(): void {
    const id = this.shipment()?.id;
    if (!id) return;
    this.service.scheduleInboundShipment(id).subscribe({
      next: () => void this.reload(id),
      error: () => this.error.set(this.t('catalog.inbound.scheduleFailed'))
    });
  }

  receive(): void {
    const s = this.shipment();
    if (!s || !this.receiveForm.lineId || this.receiveForm.quantity <= 0 || !this.receiveForm.targetBinId) {
      this.error.set(this.t('catalog.inbound.requiredFields'));
      return;
    }
    this.receiveForm.idempotencyCounter += 1;
    this.service.receiveInboundLine(s.id, {
      lineId: this.receiveForm.lineId,
      quantity: this.receiveForm.quantity,
      stagingBinId: null,
      targetBinId: this.receiveForm.targetBinId,
      directPutAway: this.receiveForm.directPutAway,
      idempotencyKey: `rcv-${s.id}-${this.receiveForm.idempotencyCounter}`
    }).subscribe({
      next: () => void this.reload(s.id),
      error: () => this.error.set(this.t('catalog.inbound.receiveFailed'))
    });
  }

  completePutAway(task: WarehouseTask): void {
    this.service.completePutAwayTask(task.id, `put-${task.id}-${Date.now()}`).subscribe({
      next: () => {
        const id = this.shipment()?.id;
        if (id) void this.reload(id);
      },
      error: () => this.error.set(this.t('catalog.inbound.putAwayFailed'))
    });
  }

  loadLandedCosts(): void {
    const id = this.shipment()?.id;
    if (!id) return;
    this.costingApi.listLandedCostAllocationsByShipment(id).subscribe({
      next: rows => {
        this.landedCosts.set(rows);
        this.landedCostPermissionDenied.set(false);
      },
      error: err => {
        this.landedCosts.set([]);
        this.landedCostPermissionDenied.set(err?.status === 403);
      }
    });
  }

  selectAllocation(row: LandedCostAllocation): void {
    this.selectedAllocation.set(row);
    this.landedForm = {
      referenceNumber: row.referenceNumber,
      allocationMethod: row.allocationMethod,
      totalLandedCostAmount: row.totalLandedCostAmount,
      currencyCode: row.currencyCode,
      reason: row.reason ?? ''
    };
  }

  allocationMethodLabel(method: string): string {
    const key = `catalog.costing.landedCost.methods.${method}`;
    const translated = this.t(key);
    return translated === key ? method : translated;
  }

  private buildLineWeights(): { inboundShipmentLineId: string; weight: number }[] | null {
    if (this.landedForm.allocationMethod !== 'Weight') return null;
    return Object.entries(this.lineWeights)
      .filter(([, weight]) => weight > 0)
      .map(([inboundShipmentLineId, weight]) => ({ inboundShipmentLineId, weight }));
  }

  createLandedCost(): void {
    const shipmentId = this.shipment()?.id;
    if (!shipmentId || !this.landedForm.referenceNumber.trim()) {
      this.error.set(this.t('catalog.costing.landedCost.requiredFields'));
      return;
    }
    this.landedBusy.set(true);
    this.costingApi.createLandedCostAllocation(shipmentId, {
      referenceNumber: this.landedForm.referenceNumber.trim(),
      allocationMethod: this.landedForm.allocationMethod,
      totalLandedCostAmount: Number(this.landedForm.totalLandedCostAmount),
      currencyCode: this.landedForm.currencyCode,
      reason: this.landedForm.reason.trim() || null,
      lineWeights: this.buildLineWeights()
    }).subscribe({
      next: created => {
        this.landedBusy.set(false);
        this.selectedAllocation.set(created);
        this.loadLandedCosts();
      },
      error: () => {
        this.landedBusy.set(false);
        this.error.set(this.t('catalog.costing.landedCost.createFailed'));
      }
    });
  }

  previewLandedCost(): void {
    const id = this.selectedAllocation()?.id;
    if (!id) return;
    this.landedBusy.set(true);
    this.costingApi.previewLandedCostAllocation(id, {
      allocationMethod: this.landedForm.allocationMethod,
      totalLandedCostAmount: Number(this.landedForm.totalLandedCostAmount),
      reason: this.landedForm.reason.trim() || null,
      lineWeights: this.buildLineWeights()
    }).subscribe({
      next: row => {
        this.selectedAllocation.set(row);
        this.landedBusy.set(false);
      },
      error: () => {
        this.landedBusy.set(false);
        this.error.set(this.t('catalog.costing.landedCost.previewFailed'));
      }
    });
  }

  commitLandedCost(): void {
    const id = this.selectedAllocation()?.id;
    if (!id) return;
    this.landedBusy.set(true);
    this.costingApi.commitLandedCostAllocation(id, { idempotencyKey: crypto.randomUUID() }).subscribe({
      next: row => {
        this.selectedAllocation.set(row);
        this.landedBusy.set(false);
        this.loadLandedCosts();
      },
      error: () => {
        this.landedBusy.set(false);
        this.error.set(this.t('catalog.costing.landedCost.commitFailed'));
      }
    });
  }

  cancelLandedCost(): void {
    const id = this.selectedAllocation()?.id;
    if (!id) return;
    this.landedBusy.set(true);
    this.costingApi.cancelLandedCostAllocation(id).subscribe({
      next: row => {
        this.selectedAllocation.set(row);
        this.landedBusy.set(false);
        this.loadLandedCosts();
      },
      error: () => {
        this.landedBusy.set(false);
        this.error.set(this.t('catalog.costing.landedCost.cancelFailed'));
      }
    });
  }

  private reload(id: string, showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.error.set('');
    this.service.getInboundShipment(id).subscribe({
      next: s => {
        this.shipment.set(s);
        if (!this.receiveForm.lineId && s.lines.length > 0) {
          this.receiveForm.lineId = s.lines[0].id;
        }
        this.service.listWarehouseBins(s.warehouseId).subscribe({ next: b => this.bins.set(b) });
        this.service.listWarehouseTasks('Pending', 'PutAway').subscribe({
          next: all => {
            const lineIds = new Set(s.lines.map(l => l.id));
            this.tasks.set(all.filter(t => t.inboundShipmentLineId && lineIds.has(t.inboundShipmentLineId)));
            this.loading.set(false);
          },
          error: () => this.loading.set(false)
        });
      },
      error: () => {
        this.shipment.set(null);
        this.loading.set(false);
      }
    });
  }
}
